import React, { useEffect, useRef } from 'react';
import { trackMeta } from '../lib/metaTrack';
import Seo from './Seo';

/* -------------------------------------------------------------------------
   Termin buchen — standalone Calendly booking page. Nothing but the widget:
   no nav, no footer, no copy. The <script> from Calendly's snippet can't run
   from JSX, so we load widget.js imperatively; it scans for
   `.calendly-inline-widget` and mounts.
   ------------------------------------------------------------------------- */

const CALENDLY_URL = 'https://calendly.com/frank-balz-hambacore/15min';
const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';

export default function TerminBuchenPage() {

  // Meta: SubmitApplication — "form started" now fires here (moved off the
  // retired /funnel route). Fires once on mount; no-op until marketing consent.
  const startTracked = useRef(false);
  useEffect(() => {
    if (startTracked.current) return;
    startTracked.current = true;
    trackMeta('SubmitApplication', { customData: { content_name: 'Terminbuchung' } });
  }, []);

  useEffect(() => {
    // Already loaded (e.g. client-side navigation back to this route) → just
    // (re)initialise the inline widgets on the page.
    if (window.Calendly) {
      window.Calendly.initInlineWidgets();
      return;
    }
    // Script already injected and loading — widget.js inits on load.
    if (document.querySelector(`script[src="${CALENDLY_SCRIPT}"]`)) return;

    const script = document.createElement('script');
    script.src = CALENDLY_SCRIPT;
    script.async = true;
    document.body.appendChild(script);
    // Intentionally not removed on unmount — Calendly is a global singleton and
    // re-loading it on every visit is wasteful (and can break re-init).
  }, []);

  // Meta: CompleteRegistration — the conversion. Since there's no /danke page,
  // we listen for Calendly's `event_scheduled` postMessage (fired inside the
  // iframe when a booking is confirmed) and fire the conversion then. trackMeta
  // handles the shared event_id (browser Pixel + server CAPI) and consent gating.
  useEffect(() => {
    const bookedRef = { current: false };
    const isCalendlyMessage = (e) =>
      e.origin === 'https://calendly.com' &&
      e.data && typeof e.data === 'object' &&
      typeof e.data.event === 'string' &&
      e.data.event.indexOf('calendly.') === 0;

    const onMessage = (e) => {
      if (!isCalendlyMessage(e)) return;
      if (e.data.event === 'calendly.event_scheduled' && !bookedRef.current) {
        bookedRef.current = true;
        trackMeta('CompleteRegistration', { customData: { content_name: 'Terminbuchung' } });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <div>
      <Seo
        path="/termin-buchen"
        title="Termin buchen — hamba"
        description="Buchen Sie Ihren Termin für die kostenlose Erstanalyse."
        noindex
      />
      {/* Always-visible safety net. If the inline embed renders blank (older iOS/
          iPad Safari, tracking blockers, third-party-storage restrictions), the
          visitor can still reach Calendly's hosted page — so a broken embed
          never costs us the lead. ~44px tall (see the calc height below). */}
      <div style={{
        height: 44, boxSizing: 'border-box',
        padding: '12px 16px', textAlign: 'center',
        fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13,
        color: '#6B6E76', background: '#F5F2EC', borderBottom: '1px solid #E2DCCF',
      }}>
        Kalender wird nicht angezeigt?{' '}
        <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
           style={{ color: '#5A2A2A', fontWeight: 600 }}>
          Termin direkt buchen&nbsp;→
        </a>
      </div>
      {/* Calendly needs a DEFINITE height on this div — it sizes its iframe to
          fill it. Explicit calc(viewport − bar) instead of flex, which collapsed. */}
      <div
        className="calendly-inline-widget"
        data-url={CALENDLY_URL}
        style={{ minWidth: 320, width: '100%', height: 'calc(100vh - 44px)', minHeight: 640 }}
      />
    </div>
  );
}
